import {DecimalPipe} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, inject, input, Signal, signal} from '@angular/core';
import {toObservable, toSignal} from '@angular/core/rxjs-interop';
import {FormsModule, NgForm} from '@angular/forms';
import {ErrorService} from '@dv/kitadmin/core/errors';
import {SubmitCancelButtonsComponent} from '@dv/kitadmin/ui';
import {handleResponse, handleResponseError} from '@dv/shared/angular';
import {MandantOrganizationApi} from '@dv/shared/backend/api/mandant-organization.api';
import type {JaxUserCountByDomain} from '@dv/shared/backend/model/jax-user-count-by-domain';
import {TranslocoDirective} from '@jsverse/transloco';
import {StateService} from '@uirouter/core';
import {TooltipModule} from 'ngx-bootstrap/tooltip';
import {catchError, finalize, map, of, switchMap, tap} from 'rxjs';

@Component({
    selector: 'dv-administration-oidc-organization-form',
    imports: [
        FormsModule,
        TooltipModule,
        SubmitCancelButtonsComponent,
        TranslocoDirective,
        DecimalPipe,
    ],
    templateUrl: './administration-oidc-organization-form.component.html',
    styles: `
        :host {
            display: block;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AdministrationOidcOrganizationFormComponent {

    public mandantId = input.required<string>();

    private readonly errorService = inject(ErrorService);
    private stateService = inject(StateService);
    private readonly mandantOrganizationApi = inject(MandantOrganizationApi);

    public formState = computed(() => {
        const allDomains = this.availableDomains();
        const preselectedDomain = allDomains.toSorted((d1, d2) => d2.count - d1.count)[0];

        return {
            domain: signal(preselectedDomain?.oidcDomain ?? ''),
        };
    });
    public isLoading = signal(false);

    private availableOidcDomains$ = toObservable(this.mandantId).pipe(
        tap(() => this.isLoading.set(true)),
        switchMap(mandantId => this.mandantOrganizationApi.getAllAvailableOidcDomains$({mandantId}).pipe(
            map(response => response.items),
            catchError((error: unknown) => {
                handleResponseError(error, 'could not load available oidc domains');

                return of<JaxUserCountByDomain[]>([]);
            }),
            finalize(() => this.isLoading.set(false)),
        )),
    );

    public availableDomains: Signal<JaxUserCountByDomain[]> =
        toSignal(this.availableOidcDomains$, {initialValue: []});

    public submitForm(form: NgForm): void {
        this.errorService.clearAll();

        const formValid = !form.invalid;
        this.errorService.handleValidationError(formValid, 'ERRORS.ERR_INCOMPLETE_FORM');
        if (!formValid) {
            return;
        }

        this.isLoading.set(true);
        const request$ = this.mandantOrganizationApi.create$({
            mandantId: this.mandantId(),
            jaxCreateOrganization: {
                oidcDomain: this.formState().domain(),
            },
        });

        request$.pipe(
            handleResponse({
                next: () => this.goToAdministrationOverview(),
                finalize: () => this.isLoading.set(false),
            }),
        ).subscribe();
    }

    public goToAdministrationOverview(): void {
        this.stateService.go('base.administration.mandanten');
    }
}
